/**
 * The forms of Settings > Paid > Ad campaigns (and the Command Center's card), checked before sending with the
 * backend's rules (ads/settings/adSettings.service.ts), so a bad entry is named here:
 *
 * - Brand terms, typed one per line or comma-separated: at most 20, each 2 to 60 characters, lowercase, no repeats.
 * - Change rules: an optional baseline date, the fewest calls before a change (1 to 200) and the cooldown (0 to 90).
 * - A budget override: dollars, or empty for none on file.
 */
export const MAX_BRAND_TERMS = 20;

type Result<T> = { ok: true; input: T } | { ok: false; error: string };

export function parseBrandTerms(text: string): string[] {
  const terms = text
    .split(/[\n,]+/)
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  return [...new Set(terms)];
}

export function brandTermsInput(text: string): Result<string[]> {
  const terms = parseBrandTerms(text);
  if (terms.length > MAX_BRAND_TERMS)
    return { ok: false, error: `At most ${MAX_BRAND_TERMS} brand terms.` };
  const bad = terms.find((t) => t.length < 2 || t.length > 60);
  if (bad) return { ok: false, error: `Each brand term is 2 to 60 characters: check "${bad}".` };
  return { ok: true, input: terms };
}

export interface ChangeRulesDraft {
  baselineDate: string;
  minCallsForChange: string;
  changeCooldownDays: string;
}

export interface ChangeRulesInput {
  baselineDate: string | null;
  minCallsForChange: number;
  changeCooldownDays: number;
}

export function changeRulesInput(draft: ChangeRulesDraft): Result<ChangeRulesInput> {
  const date = draft.baselineDate.trim();
  if (date && !/^\d{4}-\d{2}-\d{2}$/.test(date))
    return { ok: false, error: 'The baseline date is a date (YYYY-MM-DD), or empty.' };
  const minCalls = Number(draft.minCallsForChange.trim());
  if (!Number.isInteger(minCalls) || minCalls < 1 || minCalls > 200)
    return {
      ok: false,
      error: 'The fewest calls before a change is a whole number from 1 to 200.',
    };
  const cooldown = Number(draft.changeCooldownDays.trim());
  if (!Number.isInteger(cooldown) || cooldown < 0 || cooldown > 90)
    return { ok: false, error: 'The wait after a change is a whole number of days from 0 to 90.' };
  return {
    ok: true,
    input: {
      baselineDate: date || null,
      minCallsForChange: minCalls,
      changeCooldownDays: cooldown,
    },
  };
}

export function budgetInput(text: string): Result<number | null> {
  const clean = text.replace(/[$,\s]/g, '');
  if (clean === '') return { ok: true, input: null };
  const n = Number(clean);
  if (!Number.isFinite(n) || n < 0 || n > 1_000_000)
    return { ok: false, error: 'The budget is a dollar amount, or empty for none on file.' };
  return { ok: true, input: Math.round(n * 100) / 100 };
}

/** The word an agency user types to unlock the settings the practice only reads. */
export const OVERRIDE_WORD = 'OVERRIDE';

export const overrideTyped = (text: string) => text.trim() === OVERRIDE_WORD;
